import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ScrollArea } from '../components/ui/scroll-area';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { toast } from 'sonner';
import axios from 'axios';
import { format } from 'date-fns';
import { 
  MessageSquare, 
  Send, 
  Loader2,
  Search,
  User
} from 'lucide-react';
import { cn } from '../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const MessagesPage = () => {
  const { user } = useAuth();
  const [threads, setThreads] = useState([]);
  const [selectedThread, setSelectedThread] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    fetchThreads();
  }, []);

  useEffect(() => {
    if (selectedThread) {
      fetchMessages(selectedThread.thread_id);
    }
  }, [selectedThread]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const fetchThreads = async () => {
    try {
      const response = await axios.get(`${API_URL}/dm/threads`);
      setThreads(response.data.threads);
      if (response.data.threads.length > 0 && !selectedThread) {
        setSelectedThread(response.data.threads[0]);
      }
    } catch (error) {
      console.error('Failed to fetch threads:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async (threadId) => {
    try {
      const response = await axios.get(`${API_URL}/dm/threads/${threadId}/messages`);
      setMessages(response.data.messages);
    } catch (error) {
      console.error('Failed to fetch messages:', error);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedThread) return;

    setSending(true);
    try {
      const recipientUid = selectedThread.member_uids.find(uid => uid !== user.uid);
      await axios.post(`${API_URL}/dm/send`, {
        recipient_uid: recipientUid,
        text: newMessage
      });
      setNewMessage('');
      fetchMessages(selectedThread.thread_id);
      fetchThreads();
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const getOtherParticipant = (thread) => {
    // In a real app, we'd fetch user details
    const otherId = thread.member_uids?.find(uid => uid !== user?.uid);
    return { uid: otherId, name: 'Member' };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <Loader2 className="w-8 h-8 animate-spin text-gold" />
      </div>
    );
  }

  return (
    <div className="h-[calc(100vh-8rem)] flex flex-col" data-testid="messages-page">
      <h1 className="font-display font-bold text-2xl text-white mb-6">Messages</h1>
      
      <div className="flex-1 flex gap-4 min-h-0">
        {/* Thread List */}
        <Card className="w-80 shrink-0 flex flex-col bg-white/5 border-white/10">
          <CardHeader className="pb-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search conversations..."
                className="pl-9 bg-white/10 border-white/20 text-white placeholder:text-white/50"
              />
            </div>
          </CardHeader>
          <CardContent className="flex-1 p-0 overflow-hidden">
            <ScrollArea className="h-full">
              {threads.length === 0 ? (
                <div className="p-4 text-center text-cream/60">
                  <MessageSquare className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No conversations yet</p>
                </div>
              ) : (
                <div className="space-y-1 p-2">
                  {threads.map((thread) => {
                    const other = getOtherParticipant(thread);
                    return (
                      <button
                        key={thread.thread_id}
                        onClick={() => setSelectedThread(thread)}
                        className={cn(
                          "w-full p-3 rounded-sm text-left transition-colors",
                          selectedThread?.thread_id === thread.thread_id
                            ? "bg-gold text-deep-green"
                            : "hover:bg-white/5 text-white"
                        )}
                        data-testid={`thread-${thread.thread_id}`}
                      >
                        <div className="flex items-center gap-3">
                          <Avatar className="w-10 h-10">
                            <AvatarFallback className="bg-fairway text-gold">
                              {other.name.charAt(0)}
                            </AvatarFallback>
                          </Avatar>
                          <div className="flex-1 min-w-0">
                            <p className="font-semibold truncate">{other.name}</p>
                            <p className={cn(
                              "text-xs truncate",
                              selectedThread?.thread_id === thread.thread_id
                                ? "text-deep-green/70"
                                : "text-white/50"
                            )}>
                              {thread.last_message_text || 'No messages'}
                            </p>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </ScrollArea>
          </CardContent>
        </Card>

        {/* Message Area */}
        <Card className="flex-1 flex flex-col bg-white/5 border-white/10">
          {selectedThread ? (
            <>
              {/* Chat Header */}
              <CardHeader className="border-b border-white/10 py-4">
                <div className="flex items-center gap-3">
                  <Avatar>
                    <AvatarFallback className="bg-gold text-deep-green">
                      {getOtherParticipant(selectedThread).name.charAt(0)}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <p className="font-semibold text-white">
                      {getOtherParticipant(selectedThread).name}
                    </p>
                  </div>
                </div>
              </CardHeader>

              {/* Messages */}
              <CardContent className="flex-1 p-4 overflow-hidden">
                <ScrollArea className="h-full pr-4">
                  <div className="space-y-4">
                    {messages.map((msg) => {
                      const isOwn = msg.sender_uid === user?.uid;
                      return (
                        <div
                          key={msg.message_id}
                          className={cn("flex", isOwn ? "justify-end" : "justify-start")}
                        >
                          <div
                            className={cn(
                              "max-w-[70%] rounded-sm px-4 py-2",
                              isOwn
                                ? "bg-gold text-deep-green"
                                : "bg-white/10 text-white"
                            )}
                          >
                            <p>{msg.text}</p>
                            <p className={cn(
                              "text-xs mt-1",
                              isOwn ? "text-deep-green/70" : "text-white/50"
                            )}>
                              {format(new Date(msg.created_at), 'h:mm a')}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                    <div ref={messagesEndRef} />
                  </div>
                </ScrollArea>
              </CardContent>

              {/* Input */}
              <div className="p-4 border-t border-white/10">
                <form onSubmit={handleSend} className="flex gap-2">
                  <Input
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder="Type a message..."
                    className="flex-1 bg-white/10 border-white/20 text-white placeholder:text-white/50"
                    data-testid="message-input"
                  />
                  <Button 
                    type="submit" 
                    className="btn-gold"
                    disabled={sending || !newMessage.trim()}
                    data-testid="send-message-btn"
                  >
                    {sending ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Send className="w-4 h-4" />
                    )}
                  </Button>
                </form>
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-cream/60">
              <div className="text-center">
                <MessageSquare className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>Select a conversation to start messaging</p>
              </div>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};

export default MessagesPage;
